import React from 'react';
import {
  PeopleOutlined as PeopleIcon,
  AssignmentOutlined as AssignmentIcon,
  BusinessOutlined as BusinessIcon,
  SchoolOutlined as SchoolIcon,
  VerifiedUserOutlined as VerifiedIcon,
  WorkOutlineOutlined as WorkIcon,
  SettingsOutlined as SettingsIcon,
  PhonelinkLockOutlined as TwoFactorIcon,
  DashboardOutlined as DashboardIcon,
  WarningAmberOutlined as IncidentIcon,
  ConfirmationNumberOutlined as TicketIcon,
  LocalShippingOutlined as ProviderIcon,
  ChatOutlined as MessageIcon,
  HistoryOutlined as ActivityLogIcon,
  HelpOutlineOutlined as HelpIcon,
  MenuBookOutlined as BookIcon,
  // ReceiptLongOutlined as InvoiceIcon, // restore when billing comes back
  WorkspacesOutlined as PortfolioIcon,
  SwapHorizOutlined as TransferIcon,
  HomeOutlined as HomeIcon,
  HourglassEmptyOutlined as InProgressIcon,
  NotificationsOutlined as NotificationIcon,
  StorefrontOutlined as MarketIcon,
  BadgeOutlined as VisitsIcon,
  DescriptionOutlined as DescriptionIcon,
  MailOutlineOutlined as InviteIcon,
  PersonRemoveOutlined as RemovalRequestIcon,
} from '@mui/icons-material';
import { UserRole } from 'shared/types';
import { getHomeProviderAdminCompany } from 'shared/utils/roleUtils';
import {
  PLATFORM_ROLES,
  CLIENT_ROLES,
  OPERATIONS_ROLES,
  APPROVER_ROLES,
  TRAINING_ACCESS_ROLES,
} from 'shared/utils/roleUtils';

// Export SettingsIcon for use in Sidebar contextual menu
export { SettingsIcon };

export interface MenuItem {
  id: string;
  title: string;
  icon: React.ReactElement | null;
  path?: string;
  roles: UserRole[];
  stringKey?: string;
  children?: MenuItem[];
  requiresModule?: 'workPermits' | 'marketplace' | 'capacitaciones';
  /**
   * Plan-feature gate. When set, the item is hidden unless the caller's
   * active subscription has `plan.features[<flag>] === true`. Platform roles
   * bypass — they aren't tied to a company subscription. The Sidebar fetches
   * features once on mount and filters the menu accordingly.
   */
  requiresPlanFeature?: 'includesMarketplace';
  /**
   * Additional URL prefixes that should also highlight this item as active.
   * Use when the menu item "owns" pages reachable via deep-links or buttons
   * but not directly listed in the sidebar (e.g. Inicio absorbs /clients,
   * /providers, /companies/:id).
   */
  parentRoutes?: string[];
}

/**
 * Platform Admin Menu Items
 * For SUPER_ADMIN and SAFETY_STAFF roles
 */
export const getPlatformMenuItems = (): MenuItem[] => [
  // ============================================================
  // PLATFORM (super_admin + safety_staff "Soporte Monitor") — shared 8-item sidebar.
  // The legacy items below are still routable by URL but no longer appear in the
  // menu — they're absorbed into the new pages.
  // ============================================================
  {
    id: 'sa-inicio',
    title: 'Inicio',
    icon: <HomeIcon />,
    path: '/inicio',
    roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
    // Note: '/companies' is intentionally NOT here. When inside a company context the other
    // sidebar items (En Proceso / Historial / etc.) get rewritten to company-scoped
    // paths and own their own highlighting. Including '/companies' here would make Inicio
    // stay highlighted everywhere inside the company shell.
    //
    // Inicio also absorbs the legacy `Resumen` parent routes — /dashboard,
    // /users, /departments, /edit, /batch-registration — so the rail keeps
    // Inicio highlighted while drilling into those super_admin-only views
    // (the standalone `Resumen` sidebar entry was dropped to match every
    // other role's "one landing item" pattern).
    parentRoutes: [
      '/clients', '/providers', '/portfolio', '/transfer-requests',
      '/admins', '/safety_staff',
      // `/users` and `/batch-registration` were removed from this list:
      // they live under the Usuarios sidebar item (sa-usuarios) now and
      // should highlight that item, not Inicio.
      //
      // `/resumen` was also removed: inside a company shell that route is
      // now owned by the "Dashboard" sidebar item (Sidebar.tsx), which
      // matches it exactly — leaving it here made Inicio AND Dashboard
      // both show active at once on /companies/:id/resumen.
      '/dashboard', '/departments', '/edit',
    ],
  },
  // Marketplace nests under Inicio per the product brief — flat sidebar makes
  // it a sibling but ordered immediately below Inicio.
  {
    id: 'sa-marketplace',
    title: 'Desempeño de Proveedores',
    icon: <MarketIcon />,
    path: '/marketplace',
    roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
    requiresModule: 'marketplace',
  },
  // (Removed `sa-resumen` from the sidebar — it duplicated `sa-inicio`. The
  // /resumen route still exists in App.tsx for any direct links; its
  // highlighting was folded into `sa-inicio.parentRoutes` above.)
  // Usuarios only shows up for platform roles when drilled into a specific
  // company. Sidebar.tsx hides it outside company context and rewrites the
  // children paths to /companies/:id/users?type=...
  {
    id: 'sa-usuarios',
    title: 'Usuarios',
    icon: <PeopleIcon />,
    path: '/users',
    roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
    stringKey: 'usuarios',
    children: [
      {
        id: 'sa-usuarios-internos',
        title: 'Internos',
        icon: null,
        path: '/users?type=internal',
        roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
      },
      {
        id: 'sa-usuarios-externos',
        title: 'Proveedores',
        icon: null,
        path: '/users?type=external',
        roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
      },
    ],
  },
  {
    id: 'sa-invitaciones',
    title: 'Invitaciones',
    icon: <InviteIcon />,
    path: '/invitaciones',
    roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
  },
  {
    id: 'sa-todos-los-usuarios',
    title: 'Todos los Usuarios',
    icon: <PeopleIcon />,
    path: '/todos-los-usuarios',
    roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
  },
  {
    id: 'sa-en-proceso',
    title: 'En Proceso',
    icon: <InProgressIcon />,
    path: '/en-proceso',
    roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
    parentRoutes: ['/library', '/service-cards', '/evaluations', '/permits', '/support-tickets', '/attention-calls', '/incident-reports'],
  },
  {
    id: 'sa-verificaciones',
    title: 'Verificaciones',
    icon: <VerifiedIcon />,
    path: '/verifications',
    roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
  },
  {
    id: 'sa-solicitudes-remocion',
    title: 'Solicitudes de Remoción',
    icon: <RemovalRequestIcon />,
    path: '/solicitudes-remocion',
    roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
  },
  // Historial relocated into Configuración (per-company + global) — the
  // sidebar entry was retired so the top of the menu stays operational.
  // Notificaciones is exposed via the bell icon in the top bar; the
  // sidebar entry was duplicative and got removed across every role.
  {
    id: 'sa-capacitaciones',
    title: 'Capacitaciones',
    icon: <SchoolIcon />,
    path: '/capacitaciones',
    roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
    parentRoutes: ['/courses', '/talentlms'],
    requiresModule: 'capacitaciones',
  },
  {
    id: 'sa-configuracion',
    title: 'Configuración',
    icon: <SettingsIcon />,
    path: '/configuracion',
    roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
    parentRoutes: ['/billing', '/general/settings'],
  },
  {
    id: 'sa-ayuda',
    title: 'Guía de uso',
    icon: <HelpIcon />,
    path: '/ayuda',
    roles: [UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF],
    parentRoutes: ['/guia'],
  },

  // ============================================================
  // Legacy items — hidden from sidebar (URLs still work where routes exist).
  // ============================================================
  {
    id: 'mi-portafolio',
    title: 'Mi Portafolio',
    icon: <PortfolioIcon />,
    path: '/portfolio',
    roles: [],
  },
  {
    id: 'transferencias',
    title: 'Transferencias',
    icon: <TransferIcon />,
    path: '/transfer-requests',
    roles: [],
  },
  {
    id: 'clientes',
    title: 'Clientes',
    icon: <BusinessIcon />,
    path: '/clients',
    roles: [],
    stringKey: 'clientes'
  },
  {
    id: 'proveedores',
    title: 'Proveedores',
    icon: <ProviderIcon />,
    path: '/providers',
    roles: [],
    stringKey: 'proveedores'
  },
  {
    id: 'usuarios-safety',
    title: 'Usuarios Soporte Monitor',
    icon: <PeopleIcon />,
    path: '/safety_staff',
    roles: [],
    stringKey: 'personal_seguridad'
  },
  {
    id: 'libreria-global',
    title: 'Permisos',
    icon: <AssignmentIcon />,
    path: '/library',
    roles: [],
    stringKey: 'libreria'
  },
  {
    id: 'logs',
    title: 'Histórico',
    icon: <ActivityLogIcon />,
    path: '/logs',
    roles: [],
  },
  // Facturación lives inside /configuracion → "Facturación" tab. Keeping
  // /billing as a route for deep-links but not as a separate sidebar entry
  // to avoid two paths into the same view.
  {
    id: 'centro-ayuda-admin',
    title: 'Guía de uso',
    icon: <HelpIcon />,
    path: '/ayuda',
    roles: [],
  },
  {
    id: 'guia-admin',
    title: 'Guía del Sistema',
    icon: <BookIcon />,
    path: '/guia',
    roles: [],
  },
];

/**
 * Client Company Menu Items
 * For COMPANY_ADMIN, CLIENT_APPROVER, CLIENT_STAFF roles
 */
export const getClientMenuItems = (): MenuItem[] => [
  // ============================================================
  // COMPANY_ADMIN — 9-item sidebar
  // (Inicio · Consulta y Verificaciones · Usuarios · Información ·
  //  Estado de Tareas · Historial · Notificaciones · Seguridad · Ayuda)
  // Notificaciones / Seguridad / Ayuda come from the shared menu so don't
  // duplicate them here.
  // ============================================================
  {
    id: 'sup-inicio',
    title: 'Inicio',
    icon: <DashboardIcon />,
    path: '/my-company',
    roles: [UserRole.COMPANY_ADMIN],
    stringKey: 'inicio',
  },
  // Marketplace — Business-plan only. The sidebar drops this entry for
  // companies on Basic plans (BE returns 403 if reached via direct URL).
  {
    id: 'sup-marketplace',
    title: 'Desempeño de Proveedores',
    icon: <MarketIcon />,
    path: '/marketplace',
    roles: [UserRole.COMPANY_ADMIN],
    requiresPlanFeature: 'includesMarketplace',
    requiresModule: 'marketplace',
  },
  // Mis Clientes — proveedor-only (filtered in the categorizer below via
  // isProveedorCompany, same discriminator sup-marketplace uses). The
  // client companies this provider serves; not meaningful for a cliente
  // company_admin, who has no "clients" of their own.
  {
    id: 'sup-mis-clientes',
    title: 'Mis Clientes',
    icon: <BusinessIcon />,
    path: '/my-company/clientes',
    roles: [UserRole.COMPANY_ADMIN],
  },
  // Verificaciones used to have its own sidebar entry — folded into
  // Usuarios as a tab now (see UsersAndVerificationsTabs at the
  // /my-company/team route). Keeping the /my-company/verifications
  // URL routable so deep links still work, but it's no longer in
  // the company_admin sidebar.
  {
    id: 'sup-usuarios',
    title: 'Usuarios',
    icon: <PeopleIcon />,
    path: '/my-company/team',
    roles: [UserRole.COMPANY_ADMIN],
    stringKey: 'usuarios',
    children: [
      {
        id: 'sup-usuarios-internos',
        title: 'Internos',
        icon: null,
        path: '/my-company/team?type=internal',
        roles: [UserRole.COMPANY_ADMIN],
      },
      {
        id: 'sup-usuarios-externos',
        title: 'Proveedores',
        icon: null,
        path: '/my-company/team?type=external',
        roles: [UserRole.COMPANY_ADMIN],
      },
    ],
  },
  {
    id: 'sup-visitas',
    title: 'Visitas',
    icon: <VisitsIcon />,
    path: '/my-company/visitas',
    roles: [UserRole.COMPANY_ADMIN],
  },
  {
    id: 'sup-solicitudes-remocion',
    title: 'Solicitudes de Remoción',
    icon: <RemovalRequestIcon />,
    path: '/solicitudes-remocion',
    roles: [UserRole.COMPANY_ADMIN],
  },
  // Bandeja de aprobación de transferencias de subordinados. Vivía en el
  // grupo de plataforma con rol COMPANY_ADMIN — condiciones mutuamente
  // excluyentes, así que no se renderizaba para nadie y un company_admin no
  // tenía forma de aprobar una transferencia salvo escribiendo la URL.
  {
    id: 'sup-supervisor-transfers',
    title: 'Transferencias de equipo',
    icon: <TransferIcon />,
    path: '/my-company/supervisor-transfers',
    roles: [UserRole.COMPANY_ADMIN],
  },
  {
    id: 'sup-informacion',
    title: 'Información',
    icon: <IncidentIcon />,
    path: '/my-company/informacion',
    roles: [UserRole.COMPANY_ADMIN],
    parentRoutes: [
      '/my-company/incident-reports',
      '/my-company/attention-calls',
      '/my-company/support-tickets',
    ],
  },
  {
    id: 'sup-documentos',
    title: 'Documentos',
    icon: <DescriptionIcon />,
    path: '/my-company/documentos',
    roles: [UserRole.COMPANY_ADMIN],
  },
  {
    id: 'sup-tareas',
    title: 'Estado de Tareas',
    icon: <AssignmentIcon />,
    path: '/library',
    roles: [UserRole.COMPANY_ADMIN],
    stringKey: 'libreria',
    requiresModule: 'workPermits',
  },
  {
    id: 'sup-capacitaciones',
    title: 'Capacitaciones',
    icon: <SchoolIcon />,
    path: '/my-company/cursos',
    roles: [UserRole.COMPANY_ADMIN],
    requiresModule: 'capacitaciones',
  },
  // sup-historial moved into Configuración → tab "Historial". The route
  // /my-company/historial stays mounted for direct links and the
  // embedded tab.
  // Facturación entry hidden while billing is paused for the pilot — every
  // company effectively runs on Business with no expiry, so a billing
  // surface would just expose a no-op page (and emit Alegra-shaped API
  // calls that the BE no-ops). When billing comes back, uncomment and
  // restore the matching route in App.tsx.
  // {
  //   id: 'sup-facturacion',
  //   title: 'Facturación',
  //   icon: <InvoiceIcon />,
  //   path: '/my-company/billing',
  //   roles: [UserRole.COMPANY_ADMIN],
  // },
  {
    id: 'sup-configuracion',
    title: 'Configuración',
    icon: <SettingsIcon />,
    path: '/my-company/settings',
    roles: [UserRole.COMPANY_ADMIN],
    stringKey: 'configuracion',
  },

  // CLIENT_STAFF (Personal) — 4-entry sidebar per role spec.
  {
    id: 'staff-inicio',
    title: 'Inicio',
    icon: <HomeIcon />,
    path: '/staff/inicio',
    roles: [UserRole.CLIENT_STAFF],
    stringKey: 'inicio',
    parentRoutes: ['/staff/dashboard'],
  },
  // Consulta is no longer surfaced in the sidebar — its content is
  // inlined into /staff/inicio per the role spec. The /staff/consulta
  // route stays mounted so existing deep links keep working, but the
  // empty `roles` array hides it from the categorized menu.
  {
    id: 'staff-consulta',
    title: 'Consulta',
    icon: <VerifiedIcon />,
    path: '/staff/consulta',
    roles: [],
    parentRoutes: ['/my-verifications', '/courses'],
  },
  {
    id: 'staff-tareas',
    title: 'Tareas en proceso',
    icon: <WorkIcon />,
    path: '/staff/tareas',
    roles: [UserRole.CLIENT_STAFF],
    requiresModule: 'workPermits',
    parentRoutes: ['/staff/permits', '/staff/evaluations', '/staff/service-cards'],
  },
  {
    id: 'staff-historial',
    title: 'Historial',
    icon: <ActivityLogIcon />,
    path: '/staff/historial',
    roles: [UserRole.CLIENT_STAFF],
  },

  // ─── Supervisor capability for client_staff ───────────────────────
  // After unification a "Supervisor" is any client_staff user with a
  // non-empty `User.manages` array. The categorizer below adds these
  // entries on top of the regular client_staff sidebar when manages is
  // non-empty. Routes mirror the Administrador (company_admin) ones; the BE
  // `supervised` data scope narrows results to the supervisor's direct
  // reports automatically.
  {
    id: 'team-sup-usuarios',
    title: 'Mi Equipo',
    icon: <PeopleIcon />,
    path: '/staff/team',
    roles: [UserRole.CLIENT_STAFF],
  },

  // ─── "Admin Proveedor" capability for client_staff ─────────────────
  // A colaborador who is ALSO the (active, non-external) company_admin of
  // their own provider company — just currently lent out and active here
  // as client_staff (see getHomeProviderAdminCompany). The categorizer
  // below adds this on top of the regular client_staff sidebar only for
  // that person; a plain colaborador never sees it.
  {
    id: 'staff-mi-proveedor',
    // "Mis Clientes", no "Mi Proveedor": el único usuario que ve esta entrada
    // ES el admin de la proveedora, y lo que hay detrás es la lista de sus
    // empresas cliente — llamarla "Mi Proveedor" le hablaba como si fuera un
    // colaborador contratado (issue 12).
    title: 'Mis Clientes',
    icon: <ProviderIcon />,
    path: '/staff/mi-proveedor/clientes',
    roles: [UserRole.CLIENT_STAFF],
  },
];

// IDs of the menu items that only apply to client_staff users acting as
// supervisors (i.e. `User.manages.length > 0`). The categorizer below filters
// them out for regular staff.
const SUPERVISOR_ONLY_CLIENT_STAFF_IDS = new Set([
  'team-sup-usuarios',
]);

/**
 * (Removed 2026-05) Contractor menu — the contratista_* roles were unified
 * into company_admin / client_staff at proveedor companies. Provider-side
 * users now use the regular client menu; contractor-only items (Mi Equipo,
 * Usuarios Disponibles) appear in `getClientMenuItems` and are surfaced to
 * provider company_admins via the categorizer.
 */

/**
 * Operations Menu Items
 * For SECURITY role
 */
export const getOperationsMenuItems = (): MenuItem[] => [
  // ============================================================
  // SEGURIDAD — RECEPCIÓN sidebar
  // Reordered: Inicio · Lector QR · Visitas · Consulta · Usuarios ·
  //            Notificaciones · Seguridad
  // QR/Visitas come first because they're the guard's primary tools
  // at the gate. Visitas is now its own page (was a chip inside
  // Usuarios) so guests are easier to find without scrolling past
  // employee tables.
  // ============================================================
  {
    id: 'sec-inicio',
    title: 'Inicio',
    icon: <HomeIcon />,
    path: '/seguridad/inicio',
    roles: [UserRole.SECURITY],
  },
  {
    id: 'sec-qr',
    title: 'Lector QR',
    icon: <VerifiedIcon />,
    path: '/seguridad/qr',
    roles: [UserRole.SECURITY],
  },
  {
    id: 'sec-visitas',
    title: 'Visitas',
    icon: <PeopleIcon />,
    path: '/seguridad/visitas',
    roles: [UserRole.SECURITY],
  },
  {
    id: 'sec-consulta',
    title: 'Consulta',
    icon: <VerifiedIcon />,
    path: '/seguridad/consulta',
    roles: [UserRole.SECURITY],
  },
  {
    id: 'usuarios-seguridad',
    title: 'Usuarios',
    icon: <PeopleIcon />,
    path: '/guard/users',
    roles: [UserRole.SECURITY],
    stringKey: 'usuarios',
    parentRoutes: ['/security/guest-search'],
  },
  {
    id: 'sec-notificaciones',
    title: 'Notificaciones',
    icon: <NotificationIcon />,
    path: '/notificaciones',
    // Hidden — bell icon in the top bar covers this. Kept in the
    // catalog so the parentRoutes resolver doesn't break for legacy
    // deep links; empty roles array keeps it out of the sidebar.
    roles: [],
  },
  {
    id: 'sec-seguridad',
    title: 'Seguridad',
    icon: <SettingsIcon />,
    path: '/security',
    roles: [UserRole.SECURITY],
  },
  // Ayuda is provided by the shared menu — don't add a duplicate here.
  // Legacy — hidden, URL still works
  {
    id: 'buscar-invitados',
    title: 'Buscar Invitados',
    icon: <VerifiedIcon />,
    path: '/security/guest-search',
    roles: [],
  },
];

/**
 * Approver Menu Items
 * For CLIENT_APPROVER role — mirrors the company_admin structure minus
 * Usuarios and Consulta y Verificaciones, plus Ayuda. Every list page is
 * automatically scoped to "items assigned to me as approver" via the BE
 * data scope — same URLs, same components, narrower data than what
 * company_admin sees.
 */
export const getApproverMenuItems = (): MenuItem[] => [
  {
    id: 'approver-inicio',
    title: 'Inicio',
    icon: <DashboardIcon />,
    path: '/approver/dashboard',
    roles: [...APPROVER_ROLES],
    stringKey: 'inicio',
  },
  // Información was removed — the dashboard already surfaces every
  // approver-actionable item (fichas / evaluaciones / permisos) so
  // there was no second page to route to. The entry used to land on
  // /my-company/informacion which is blocked for CLIENT_APPROVER by
  // the /my-company parent gate.
  {
    id: 'approver-biblioteca',
    title: 'Biblioteca',
    icon: <BookIcon />,
    path: '/library',
    roles: [...APPROVER_ROLES],
    stringKey: 'libreria',
    requiresModule: 'workPermits',
  },
  {
    id: 'approver-ayuda',
    title: 'Guía de uso',
    icon: <HelpIcon />,
    path: '/ayuda',
    roles: [...APPROVER_ROLES],
  },
];

/**
 * Shared Menu Items (accessible by multiple role categories)
 */
export const getSharedMenuItems = (): MenuItem[] => [
  {
    id: 'mis-verificaciones',
    title: 'Mis Verificaciones',
    icon: <VerifiedIcon />,
    path: '/my-verifications',
    // Security guards verify OTHERS at the gate — they don't maintain
    // their own verification documents. Approvers + platform admins are
    // also exempt at the data layer (calculateUserVerificationStatus).
    roles: [...CLIENT_ROLES],
    stringKey: 'mis_verificaciones'
  },
  {
    id: 'cursos',
    title: 'Mis Cursos',
    icon: <SchoolIcon />,
    path: '/courses',
    roles: [...TRAINING_ACCESS_ROLES],
    stringKey: 'cursos',
    requiresModule: 'capacitaciones',
  },
  {
    id: 'seguridad',
    title: 'Seguridad',
    icon: <TwoFactorIcon />,
    path: '/security',
    // Admin roles (company_admin, client_approver) reach 2FA from the
    // Configuración → "Seguridad 2FA" tab. Colaboradores (client_staff)
    // don't have access to CompanySettings, so they keep this entry as
    // their 2FA gateway.
    roles: [UserRole.CLIENT_STAFF],
    stringKey: 'seguridad'
  },
  {
    id: 'shared-notificaciones',
    title: 'Notificaciones',
    icon: <NotificationIcon />,
    path: '/notificaciones',
    // Hidden — Notificaciones is exposed via the bell icon in the top
    // bar across every role. Kept in the catalog so parentRoutes
    // resolution doesn't break for legacy deep links.
    roles: [],
  },
  {
    id: 'mensajes',
    title: 'Mensajes',
    icon: <MessageIcon />,
    path: '/mensajes',
    roles: [...PLATFORM_ROLES, ...CLIENT_ROLES, UserRole.SECURITY],
  },
  {
    id: 'tickets-soporte',
    title: 'Soporte',
    icon: <TicketIcon />,
    path: '/support-tickets',
    roles: [...PLATFORM_ROLES],
    stringKey: 'soporte'
  },
  {
    id: 'centro-ayuda',
    title: 'Guía de uso',
    icon: <HelpIcon />,
    path: '/ayuda',
    roles: [...PLATFORM_ROLES, ...CLIENT_ROLES, UserRole.SECURITY],
  },
];

/**
 * Get all menu items (for backwards compatibility)
 */
export const useMenuItems = (): MenuItem[] => {
  return [
    ...getPlatformMenuItems(),
    ...getClientMenuItems(),
    ...getOperationsMenuItems(),
    ...getSharedMenuItems(),
  ];
};

/**
 * Get menu items filtered by user's role
 */
export const getMenuItemsForRole = (userRole: UserRole): MenuItem[] => {
  const allItems = [
    ...getPlatformMenuItems(),
    ...getClientMenuItems(),
    ...getOperationsMenuItems(),
    ...getSharedMenuItems(),
  ];

  return allItems.filter(item => item.roles.includes(userRole));
};

/**
 * Get categorized menu items for a specific role
 * Returns items grouped by category for better sidebar organization
 */
export const getCategorizedMenuItems = (
  userRole: UserRole,
  hasRole: (role: UserRole | UserRole[]) => boolean,
  user?: any,
): { category: string; items: MenuItem[] }[] => {
  const categories: { category: string; items: MenuItem[] }[] = [];

  // CLIENT_STAFF — simplified sidebar. With `User.manages.length > 0` the
  // user is a Supervisor (formerly client_team_supervisor) and gets the
  // extra Usuarios + Información entries (gated by SUPERVISOR_ONLY_CLIENT_STAFF_IDS).
  if (hasRole(UserRole.CLIENT_STAFF) && !hasRole([UserRole.COMPANY_ADMIN])) {
    const isSupervisor = ((user?.manages as any[]) || []).length > 0;
    const isHomeProviderAdmin = !!getHomeProviderAdminCompany(user);
    const staffItems = getClientMenuItems().filter((item) => {
      if (!item.roles.includes(UserRole.CLIENT_STAFF)) return false;
      if (
        SUPERVISOR_ONLY_CLIENT_STAFF_IDS.has(item.id) &&
        !isSupervisor
      ) {
        return false;
      }
      if (item.id === 'staff-mi-proveedor' && !isHomeProviderAdmin) return false;
      return true;
    });
    // Admin de una proveedora prestado a un cliente: gestiona las Fichas,
    // Evaluaciones y Permisos de ESE cliente (capacidad, no rol — su
    // membresía externa sigue siendo client_staff, así que ninguna entrada
    // por rol lo alcanza). Sin esto la función existe pero no hay forma de
    // llegar a ella: /library sólo se abre escribiendo la URL.
    // Se muestra con la capacidad que manda el backend O, como respaldo,
    // cuando el propio `companies[]` ya dice que es el admin de su empresa
    // proveedora y está prestado acá (isHomeProviderAdmin). Lo segundo evita
    // que la entrada desaparezca si la sesión es vieja o el flag todavía no
    // llegó: el menú es navegación, el permiso real lo aplica el servidor en
    // cada endpoint.
    if (user?.canManageWorkDocuments || isHomeProviderAdmin) {
      const permisosItem: MenuItem = {
        id: 'staff-permisos-proveedor',
        title: 'Permisos',
        icon: <AssignmentIcon />,
        path: '/library',
        roles: [UserRole.CLIENT_STAFF],
        stringKey: 'libreria',
        requiresModule: 'workPermits',
      };
      const afterTareas = staffItems.findIndex((i) => i.id === 'staff-tareas');
      staffItems.splice(afterTareas >= 0 ? afterTareas + 1 : staffItems.length, 0, permisosItem);
    }

    // Información (accidentes / sanciones / tickets) del propio personal.
    // Los accidentes y sanciones de sus trabajadores se levantan en sitio del
    // CLIENTE, así que sólo se pueden consultar desde adentro del cliente; el
    // BE ya acota el listado a los registros donde participa alguien de su
    // empresa. Va por isHomeProviderAdmin y no por canManageWorkDocuments:
    // esto no es una capacidad sobre documentos de trabajo del cliente, es
    // información sobre su propia gente.
    if (isHomeProviderAdmin) {
      const informacionItem: MenuItem = {
        id: 'staff-informacion-proveedor',
        title: 'Información',
        icon: <IncidentIcon />,
        path: '/staff/informacion',
        roles: [UserRole.CLIENT_STAFF],
      };
      const afterHistorial = staffItems.findIndex((i) => i.id === 'staff-historial');
      staffItems.splice(
        afterHistorial >= 0 ? afterHistorial + 1 : staffItems.length,
        0,
        informacionItem,
      );
    }

    if (staffItems.length > 0) {
      categories.push({ category: 'Principal', items: staffItems });
    }

    const PERSONAL_SHARED_IDS = new Set([
      'shared-notificaciones',
      'seguridad',
      'centro-ayuda',
    ]);
    const sharedItems = getSharedMenuItems().filter(item =>
      PERSONAL_SHARED_IDS.has(item.id) &&
      item.roles.some(role => hasRole(role))
    );
    if (sharedItems.length > 0) {
      categories.push({ category: 'Personal', items: sharedItems });
    }

    return categories;
  }

  // Approver-only items (CLIENT_APPROVER gets a simplified sidebar)
  if (hasRole([...APPROVER_ROLES]) && !hasRole([UserRole.COMPANY_ADMIN])) {
    const approverItems = getApproverMenuItems().filter(item =>
      item.roles.some(role => hasRole(role))
    );
    if (approverItems.length > 0) {
      categories.push({ category: 'Aprobador', items: approverItems });
    }

    // Personal section — approvers don't maintain their own verifications
    // (they approve OTHER people's work), so "Mis Verificaciones" is
    // intentionally excluded here. Notificaciones is in the top-bar bell
    // and Seguridad lives under Configuración → Seguridad 2FA, so both
    // are stripped from the sidebar via empty/tightened roles in the
    // shared catalog above — they still appear in the ID set so legacy
    // persisted preferences don't break, but the role filter below
    // hides them.
    const APPROVER_PERSONAL_IDS = new Set([
      'cursos',
      'shared-notificaciones',
      'mensajes',
      'seguridad',
    ]);
    const sharedItems = getSharedMenuItems().filter(item =>
      APPROVER_PERSONAL_IDS.has(item.id) &&
      item.roles.some(role => hasRole(role))
    );
    if (sharedItems.length > 0) {
      categories.push({ category: 'Personal', items: sharedItems });
    }

    return categories; // Return early for approver - they only see approver items + personal
  }

  // Platform items
  if (hasRole([...PLATFORM_ROLES])) {
    const platformItems = getPlatformMenuItems().filter(item =>
      item.roles.some(role => hasRole(role))
    );
    if (platformItems.length > 0) {
      categories.push({ category: 'Administración', items: platformItems });
    }
  }

  // Client items
  if (hasRole([...CLIENT_ROLES])) {
    // Marketplace is a buyer-side surface — proveedor company_admins
    // should not see it (they're the ones being shopped, not shopping).
    // The discriminator is the active company's companyType.
    const isProveedorCompany = user?.company?.companyType === 'proveedor';
    const clientItems = getClientMenuItems().filter(item => {
      if (!item.roles.some(role => hasRole(role))) return false;
      if (isProveedorCompany && item.id === 'sup-marketplace') return false;
      if (!isProveedorCompany && item.id === 'sup-mis-clientes') return false;
      return true;
    });
    if (clientItems.length > 0) {
      categories.push({ category: 'Mi Empresa', items: clientItems });
    }
  }

  // (Removed) Dedicated CONTRACTOR sidebar branch — provider-side users now
  // share the company_admin / client_staff sidebars; the BE scopes data by
  // companyType.

  // Operations items
  if (hasRole([...OPERATIONS_ROLES])) {
    const opsItems = getOperationsMenuItems().filter(item =>
      item.roles.some(role => hasRole(role))
    );
    if (opsItems.length > 0) {
      categories.push({ category: 'Operaciones', items: opsItems });
    }
  }

  // Shared items - skip for platform roles (they have their own entries).
  // COMPANY_ADMIN previously had `shared-notificaciones` + `seguridad`
  // from this bucket. After the sidebar trim:
  //   - shared-notificaciones is hidden across every role (top-bar bell).
  //   - `seguridad` is restricted to CLIENT_STAFF (admins reach 2FA via
  //     Configuración → Seguridad 2FA tab).
  // So in practice the COMPANY_ADMIN's Personal/General category will
  // come back empty here and not get pushed. The filter is kept as-is
  // because role membership on each shared item is the source of truth.
  if (!hasRole([...PLATFORM_ROLES])) {
    const SUPERVISOR_SHARED_ALLOW = new Set([
      'shared-notificaciones',
      'seguridad',
    ]);
    const isSupervisor = hasRole([UserRole.COMPANY_ADMIN]);
    const sharedItems = getSharedMenuItems().filter((item) => {
      if (!item.roles.some((role) => hasRole(role))) return false;
      if (isSupervisor && !SUPERVISOR_SHARED_ALLOW.has(item.id)) return false;
      return true;
    });
    if (sharedItems.length > 0) {
      categories.push({ category: 'General', items: sharedItems });
    }
  }

  return categories;
};
